import {
  APPOINTMENT_CHECKOUT_STATUSES,
  AppointmentStatus,
  DEFAULT_MOH_CAPITATION_BAND,
  DEFAULT_RECALL_PERIOD_IN_MONTHS,
  EmailActionType,
  EmailResourceType,
  Gender,
  IAccountCreditExtendedData,
  IAppointment,
  IAutomatedNotification,
  IAutomation,
  IAutomationResource,
  IBasePatient,
  IBrand,
  IClinicalChart,
  IContactNumber,
  ICustomFormConfiguration,
  IDisplayAge,
  IEmailReplyTokenData,
  IFeeSchedule,
  IGeneratedTask,
  IInteractionV2,
  IInvoice,
  ILabJob,
  IMarketingCampaignEntry,
  IMedia,
  IMohCapitationBand,
  IOrganisation,
  IPatient,
  IPatientContactDetails,
  IPatientDocument,
  IPatientForm,
  IPatientMetadata,
  IPatientRelationship,
  IPractice,
  IPrescription,
  IStaffer,
  ISuggestedPatientData,
  ITag,
  ITask,
  ITransaction,
  IsPrimaryContact,
  MOH_CAPITATION_BANDS,
  MarketingCampaignCollection,
  MohSchoolException,
  NOT_ENROLLED_MOH_CAPITATION_BAND,
  PatientCollection,
  PatientFormSpecialUid,
  PatientFormType,
  PatientMetadataValue,
  PatientRelationshipType,
  PatientStatus,
  PrescriptionStatus,
  PrincipleCreatedBy,
  RECEIVE_MARKETING_METADATA_KEY,
  TaskStatus,
  TransactionProvider,
  TreatmentStepAutomation,
  WithPrimaryContact,
  initAccountSummary,
  isPatientContactDetails,
  isPatientWithPrimaryContact,
} from '@principle-theorem/principle-core/interfaces';
import {
  ArchivedDocument,
  CollectionReference,
  DAY_MONTH_YEAR_FORMAT,
  DocumentArchive,
  DocumentReference,
  Firestore,
  IReffable,
  RequireProps,
  TimeUnit,
  WithRef,
  addDoc,
  all$,
  asColRef,
  asReffable,
  asyncForEach,
  collectionGroupQuery,
  doc,
  firstResult,
  firstResult$,
  getDoc,
  getDocs,
  isSameRef,
  isWithRef,
  multiFilter,
  multiSort,
  multiSwitchMap,
  orderBy,
  query,
  query$,
  reduce2DArray,
  reduceToSingleArray,
  safeCombineLatest,
  saveDoc,
  snapshot,
  sortByCreatedAt,
  sortByUpdatedAt,
  sortTimestampAsc,
  subCollection,
  toMoment,
  toNamedDocument,
  toTimestamp,
  uid,
  undeletedFilter,
  undeletedQuery,
  where,
} from '@principle-theorem/shared';
import { createCSRF } from '@principle-theorem/temporary-tokens';
import { compact, first, flatMap, get, set } from 'lodash';
import * as moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { Observable, combineLatest, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Appointment } from '../appointment/appointment';
import { Brand } from '../brand';
import { ClinicalChart } from '../clinical-charting/clinical-chart';
import { TreatmentPlan } from '../clinical-charting/treatment/treatment-plan';
import { Invoice } from '../invoice/invoice';
import { LabJob } from '../lab-job/lab-job';
import { MarketingCampaignEntry } from '../marketing/campaign-entry';
import { Organisation } from '../organisation/organisation';
import { OrganisationCache } from '../organisation/organisation-cache';
import { Task } from '../task/task';
import {
  findMobileNumber,
  getPhoneNumberVariants,
  hasContactableNumber,
  hasValidContactDetails,
} from './patient-contact-details';
import { PatientRelationship } from './patient-relationship';
import { Recalls } from './recalls';

export class Patient {
  static init(overrides: Partial<IPatient>): IPatient {
    return {
      name: '',
      address: '',
      relationships: [],
      status: PatientStatus.Active,
      statusHistory: [],
      email: '',
      gender: Gender.NotSpecified,
      contactNumbers: [],
      notes: [],
      tags: [],
      metadata: {},
      accountSummary: initAccountSummary(),
      deleted: false,
      recall: Recalls.initPatientRecall({
        periodInMonths: DEFAULT_RECALL_PERIOD_IN_MONTHS,
        treatmentRefs: [],
      }),
      secondaryRecall: Recalls.initPatientRecall({
        periodInMonths: DEFAULT_RECALL_PERIOD_IN_MONTHS,
        treatmentRefs: [],
      }),
      ...overrides,
    };
  }

  static brandRef(patient: IReffable<IPatient>): DocumentReference<IBrand> {
    return Firestore.getParentDocRef<IBrand>(patient.ref);
  }

  static primaryContact$(
    patient: IPatient & WithPrimaryContact
  ): Observable<WithRef<IBasePatient & IsPrimaryContact>> {
    return Firestore.doc$(patient.primaryContact.patient.ref);
  }

  static primaryContact(
    patient: IPatient & WithPrimaryContact
  ): Promise<WithRef<IBasePatient & IsPrimaryContact>> {
    return OrganisationCache.patients.getDoc(
      patient.primaryContact.patient.ref
    ) as Promise<WithRef<IBasePatient & IsPrimaryContact>>;
  }

  static preferredDentist$(
    patient: IPatient
  ): Observable<WithRef<IStaffer> | undefined> {
    if (!patient.preferredDentist) {
      return of(undefined);
    }
    return OrganisationCache.staff.get.doc$(patient.preferredDentist.ref);
  }

  static preferredHygienist$(
    patient: IPatient
  ): Observable<WithRef<IStaffer> | undefined> {
    if (!patient.preferredHygienist) {
      return of(undefined);
    }
    return OrganisationCache.staff.get.doc$(patient.preferredHygienist.ref);
  }

  static preferredPractice$(
    patient: IPatient
  ): Observable<WithRef<IPractice> | undefined> {
    if (!patient.preferredPractice) {
      return of(undefined);
    }
    return OrganisationCache.practices.doc$(patient.preferredPractice.ref);
  }

  static preferredFeeSchedule$(
    patient: IPatient
  ): Observable<WithRef<IFeeSchedule> | undefined> {
    if (!patient.preferredFeeSchedule) {
      return of(undefined);
    }
    return Firestore.doc$(patient.preferredFeeSchedule.ref);
  }

  static appointmentCol(
    patient: IReffable<IPatient>
  ): CollectionReference<IAppointment> {
    return subCollection<IAppointment>(
      patient.ref,
      PatientCollection.Appointments
    );
  }

  static appointments$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IAppointment>[]> {
    return all$(undeletedQuery(Patient.appointmentCol(patient)));
  }

  static appointments(
    patient: IReffable<IPatient>
  ): Promise<WithRef<IAppointment>[]> {
    return Firestore.getDocs(undeletedQuery(Patient.appointmentCol(patient)));
  }

  static archiveCol(
    patient: WithRef<IPatient>
  ): CollectionReference<ArchivedDocument<IPatient>> {
    return subCollection<ArchivedDocument<IPatient>>(
      patient.ref,
      PatientCollection.PatientDetailsHistory
    );
  }

  static clinicalChartsCol(
    patient: IReffable<IPatient>
  ): CollectionReference<IClinicalChart> {
    return subCollection<IClinicalChart>(
      patient.ref,
      PatientCollection.ClinicalCharts
    );
  }

  static patientDocumentCol(
    patient: IReffable<IPatient>
  ): CollectionReference<IPatientDocument> {
    return subCollection<IPatientDocument>(
      patient.ref,
      PatientCollection.PatientDocuments
    );
  }

  static patientDocuments$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IPatientDocument>[]> {
    return query$(undeletedQuery(Patient.patientDocumentCol(patient))).pipe(
      map((documents) => documents.sort(sortByCreatedAt))
    );
  }

  static prescriptionCol(
    patient: IReffable<IPatient>
  ): CollectionReference<IPrescription> {
    return subCollection<IPrescription>(
      patient.ref,
      PatientCollection.Prescriptions
    );
  }

  static prescriptions$(
    patient: WithRef<IPatient>,
    status?: PrescriptionStatus
  ): Observable<WithRef<IPrescription>[]> {
    const statusConstraint = status ? where('status', '==', status) : undefined;
    return Patient.withPatientRelationships$(
      patient,
      [PatientRelationshipType.DuplicatePatient],
      (patientReffable) =>
        query$(
          undeletedQuery(Patient.prescriptionCol(patientReffable)),
          ...compact([statusConstraint])
        )
    ).pipe(map((prescription) => prescription.sort(sortByCreatedAt)));
  }

  static formCol(
    patient: IReffable<IPatient>
  ): CollectionReference<IPatientForm> {
    return subCollection<IPatientForm>(
      patient.ref,
      PatientCollection.SubmittedForms
    );
  }

  static forms$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IPatientForm>[]> {
    return all$(Patient.formCol(patient));
  }

  static formsFromTemplate(
    patient: IReffable<IPatient>,
    templateRef: DocumentReference<ICustomFormConfiguration>
  ): Promise<WithRef<IPatientForm>[]> {
    return query(
      undeletedQuery(Patient.formCol(patient)),
      where('formType', '==', PatientFormType.CustomForm),
      where('template.ref', '==', templateRef)
    );
  }

  static formsFromTemplate$(
    patient: IReffable<IPatient>,
    templateRef: DocumentReference<ICustomFormConfiguration>
  ): Observable<WithRef<IPatientForm>[]> {
    return query$(
      undeletedQuery(Patient.formCol(patient)),
      where('formType', '==', PatientFormType.CustomForm),
      where('template.ref', '==', templateRef)
    );
  }

  static medicalHistoryForms$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IPatientForm>[]> {
    const defaultFormRef = doc(
      Patient.formCol(patient),
      PatientFormSpecialUid.MedicalHistoryForm
    );
    const defaultForm$ = from(Firestore.safeGetDoc(defaultFormRef));

    const medicalHistoryForms$ = this.formsFromTemplate$(
      patient,
      Brand.medicalHistoryFormRef({ ref: Patient.brandRef(patient) })
    );

    return combineLatest([medicalHistoryForms$, defaultForm$]).pipe(
      map((forms) => compact(forms.flat()).sort(sortByUpdatedAt))
    );
  }

  static linkedMedicalHistoryForms$(
    patient: WithRef<IPatient>
  ): Observable<WithRef<IPatientForm>[]> {
    return Patient.withPatientRelationships$(
      patient,
      [PatientRelationshipType.DuplicatePatient],
      (linkedPatient) => Patient.medicalHistoryForms$(linkedPatient)
    );
  }

  static patientDetailsForms$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IPatientForm>[]> {
    const formCol = Patient.formCol(patient);
    return query$(
      undeletedQuery(formCol),
      where('formType', '==', PatientFormType.PatientDetailsForm)
    ).pipe(map((forms) => forms.sort(sortByUpdatedAt)));
  }

  static creditTransactions$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<ITransaction<IAccountCreditExtendedData>>[]> {
    return Patient.invoices$(patient).pipe(
      multiSwitchMap((invoice) =>
        query$(
          undeletedQuery(
            asColRef<ITransaction<IAccountCreditExtendedData>>(
              Invoice.transactionCol(invoice)
            )
          ),
          where('provider', 'in', [
            TransactionProvider.AccountCredit,
            TransactionProvider.AccountCreditTransfer,
          ])
        )
      ),
      reduce2DArray()
    );
  }

  static invoiceCol(
    patient: IReffable<IPatient>
  ): CollectionReference<IInvoice> {
    return subCollection<IInvoice>(patient, PatientCollection.Invoices);
  }

  static invoices$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IInvoice>[]> {
    return query$(undeletedQuery(Patient.invoiceCol(patient)));
  }

  static historyCol(
    patient: IReffable<IPatient>
  ): CollectionReference<IInteractionV2> {
    return subCollection<IInteractionV2>(
      patient.ref,
      PatientCollection.History
    );
  }

  static history$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IInteractionV2>[]> {
    return all$(undeletedQuery(Patient.historyCol(patient))).pipe(
      map((interactions) =>
        interactions.sort((interactionA, interactionB) =>
          sortTimestampAsc(interactionA.createdAt, interactionB.createdAt)
        )
      )
    );
  }

  static async addInteraction(
    patient: IReffable<IPatient>,
    interaction: IInteractionV2
  ): Promise<void> {
    await addDoc(Patient.historyCol(patient), interaction);
  }

  static relationships$(
    patient: WithRef<IPatient>
  ): Observable<WithRef<IPatient>[]> {
    return safeCombineLatest(
      patient.relationships.map((data) =>
        Firestore.doc$<IPatient>(data.patient.ref)
      )
    );
  }

  static upsertRelationship(
    patient: IPatient,
    relation: IPatientRelationship
  ): void {
    const foundIndex = patient.relationships.findIndex((relationship) =>
      isSameRef(relationship.patient, relation.patient)
    );
    if (foundIndex === -1) {
      patient.relationships.push(relation);
      return;
    }
    patient.relationships[foundIndex] = relation;
  }

  static async updatePatientDetails(
    patient: WithRef<IPatient>,
    stafferRef?: DocumentReference<IStaffer>
  ): Promise<void> {
    await DocumentArchive.snapshotToArchive(
      await OrganisationCache.patients.getDoc(patient.ref),
      Patient.archiveCol(patient),
      undefined,
      stafferRef
    );

    await Firestore.saveDoc(patient);
  }

  static async markAsDuplicate(
    duplicatePatient: WithRef<IPatient>,
    primaryPatient: WithRef<IPatient>
  ): Promise<void> {
    const relationship = {
      patient: toNamedDocument(primaryPatient),
      type: PatientRelationshipType.PrimaryPatient,
    };
    Patient.upsertRelationship(duplicatePatient, relationship);
    await Firestore.patchDoc(duplicatePatient.ref, {
      status: PatientStatus.Inactive,
      relationships: duplicatePatient.relationships,
    });
    await PatientRelationship.addRelationshipBackReference(
      duplicatePatient,
      relationship
    );
    await ClinicalChart.lockClinicalCharts(duplicatePatient);
  }

  static isDuplicatePatient(patient: IPatient): boolean {
    return patient.relationships.some(
      (relationship) =>
        relationship.type === PatientRelationshipType.PrimaryPatient
    );
  }

  static async getMergedToPatient(
    patient: IPatient
  ): Promise<WithRef<IPatient> | undefined> {
    const mergedPatient = patient.relationships.find(
      (relationship) =>
        relationship.type === PatientRelationshipType.PrimaryPatient
    );

    if (!mergedPatient) {
      return;
    }

    return OrganisationCache.patients.getDoc(mergedPatient.patient.ref);
  }

  static async unmergePatient(patient: WithRef<IPatient>): Promise<void> {
    const primaryPatient = await Patient.getMergedToPatient(patient);
    if (!primaryPatient) {
      return;
    }

    await Firestore.patchDoc(patient.ref, {
      status: PatientStatus.Active,
      relationships: patient.relationships.filter(
        (relationship) =>
          relationship.type !== PatientRelationshipType.PrimaryPatient
      ),
    });

    await Firestore.patchDoc(primaryPatient.ref, {
      relationships: primaryPatient.relationships.filter(
        (relationship) =>
          relationship.type !== PatientRelationshipType.DuplicatePatient ||
          !isSameRef(relationship.patient.ref, patient.ref)
      ),
    });
  }

  static withPatientRelationships$<T>(
    patient: WithRef<IPatient>,
    includedRelationships: PatientRelationshipType[],
    queryFn: (patient: IReffable<IPatient>) => Observable<T[]>
  ): Observable<T[]> {
    return safeCombineLatest(
      Patient.relationshipRefs(patient, includedRelationships).map(
        (patientRef) => queryFn({ ref: patientRef })
      )
    ).pipe(reduce2DArray());
  }

  static relationshipRefs(
    patient: WithRef<IPatient>,
    includedRelationships: PatientRelationshipType[]
  ): DocumentReference<IPatient>[] {
    return [
      patient.ref,
      ...patient.relationships
        .filter((relationship) =>
          includedRelationships.includes(relationship.type)
        )
        .map((relationship) => relationship.patient.ref),
    ];
  }

  static mediaCol(patient: IReffable<IPatient>): CollectionReference<IMedia> {
    return subCollection<IMedia>(patient.ref, PatientCollection.Media);
  }

  static media$(patient: IReffable<IPatient>): Observable<WithRef<IMedia>[]> {
    return all$(Patient.mediaCol(patient));
  }

  static ageInYears(
    patient: Pick<IPatient, 'dateOfBirth'>
  ): number | undefined {
    if (!patient.dateOfBirth) {
      return;
    }
    return moment().diff(toMoment(patient.dateOfBirth), 'years');
  }

  static age(patient: Pick<IPatient, 'dateOfBirth'>): IDisplayAge | undefined {
    if (!patient.dateOfBirth) {
      return;
    }

    const birthDate = toMoment(patient.dateOfBirth);
    const now = moment();

    const years = now.diff(birthDate, 'years');
    if (years > 0) {
      if (years < 6) {
        const remainingMonths = now.diff(
          birthDate.clone().add(years, 'years'),
          'months'
        );
        if (remainingMonths > 0) {
          return {
            value: years,
            unit: years === 1 ? TimeUnit.Year : TimeUnit.Years,
            secondaryValue: remainingMonths,
            secondaryUnit:
              remainingMonths === 1 ? TimeUnit.Month : TimeUnit.Months,
          };
        }
      }

      return {
        value: years,
        unit: years === 1 ? TimeUnit.Year : TimeUnit.Years,
      };
    }

    const months = now.diff(birthDate, 'months');
    if (months > 0) {
      return {
        value: months,
        unit: months === 1 ? TimeUnit.Month : TimeUnit.Months,
      };
    }

    const weeks = now.diff(birthDate, 'weeks');
    if (weeks > 0) {
      return {
        value: weeks,
        unit: weeks === 1 ? TimeUnit.Week : TimeUnit.Weeks,
      };
    }

    const days = now.diff(birthDate, 'days');
    const finalValue = days || 1;
    return {
      value: finalValue,
      unit: finalValue === 1 ? TimeUnit.Day : TimeUnit.Days,
    };
  }

  static displayAge(
    patient: Pick<IPatient, 'dateOfBirth'>
  ): string | undefined {
    const ageInfo = Patient.age(patient);

    if (!ageInfo) {
      return;
    }

    if (ageInfo.secondaryValue !== undefined && ageInfo.secondaryUnit) {
      return `${ageInfo.value} ${ageInfo.unit} ${ageInfo.secondaryValue} ${ageInfo.secondaryUnit} old`;
    }

    return `${ageInfo.value} ${ageInfo.unit} old`;
  }

  static tags$(patient: IReffable<IPatient>): Observable<WithRef<ITag>[]> {
    return OrganisationCache.patients
      .doc$(patient.ref)
      .pipe(
        switchMap((latestPatient) =>
          safeCombineLatest(
            latestPatient.tags.map((tag) => Firestore.doc$(tag.ref))
          )
        )
      );
  }

  static storagePath(patient: IReffable<IPatient>): string {
    return patient.ref.path;
  }

  static getPriorAppointment$(
    patient: WithRef<IPatient>,
    appointment: RequireProps<IAppointment, 'event'>
  ): Observable<WithRef<IAppointment> | undefined> {
    return firstResult$(
      Appointment.col(patient),
      where('event.to', '<', appointment.event.from),
      orderBy('event.to', 'desc')
    );
  }

  static getSubsequentAppointment$(
    patient: WithRef<IPatient>,
    appointment: RequireProps<IAppointment, 'event'>
  ): Observable<WithRef<IAppointment> | undefined> {
    return firstResult$(
      Appointment.col(patient),
      where('event.from', '>', appointment.event.to),
      orderBy('event.from', 'asc')
    );
  }

  static lastAppointment$(
    patient: IReffable<IPatient>,
    beforeDate?: Moment,
    status?: AppointmentStatus
  ): Observable<WithRef<IAppointment> | undefined> {
    return firstResult$(
      Appointment.col(patient),
      ...compact([
        status ? where('status', '==', status) : undefined,
        beforeDate
          ? where('event.from', '<=', toTimestamp(beforeDate))
          : undefined,
        orderBy('event.from', 'desc'),
      ])
    );
  }

  static lastCompletedAppointment$(
    patient: WithRef<IPatient>,
    beforeDate?: Moment
  ): Observable<WithRef<IAppointment> | undefined> {
    return this.lastAppointment$(
      patient,
      beforeDate,
      AppointmentStatus.Complete
    );
  }

  static async lastCompletedAppointment(
    patient: IReffable<IPatient>
  ): Promise<WithRef<IAppointment> | undefined> {
    return firstResult(
      Appointment.col(patient),
      where('status', '==', AppointmentStatus.Complete),
      orderBy('event.from', 'desc')
    );
  }

  static async firstCheckedOutAppointment(
    patient: IReffable<IPatient>
  ): Promise<WithRef<IAppointment> | undefined> {
    return Firestore.firstResult(
      Appointment.col(patient),
      where('status', 'in', APPOINTMENT_CHECKOUT_STATUSES)
    );
  }

  static firstScheduledAppointment$(
    patient: WithRef<IPatient>
  ): Observable<WithRef<IAppointment> | undefined> {
    return firstResult$(
      Appointment.col(patient),
      where('status', 'in', [
        AppointmentStatus.Scheduled,
        AppointmentStatus.Confirmed,
        AppointmentStatus.Arrived,
        AppointmentStatus.CheckedIn,
        AppointmentStatus.InProgress,
        AppointmentStatus.CheckingOut,
        AppointmentStatus.Complete,
      ]),
      orderBy('event.from', 'asc')
    );
  }

  static async firstScheduledAppointment(
    patient: IReffable<IPatient>
  ): Promise<WithRef<IAppointment> | undefined> {
    return firstResult(
      Appointment.col(patient),
      where('status', 'in', [
        AppointmentStatus.Scheduled,
        AppointmentStatus.Confirmed,
        AppointmentStatus.Arrived,
        AppointmentStatus.CheckedIn,
        AppointmentStatus.InProgress,
        AppointmentStatus.CheckingOut,
        AppointmentStatus.Complete,
      ]),
      orderBy('event.from', 'asc')
    );
  }

  static async getLastCompletedAppointment(
    patient: WithRef<IPatient>
  ): Promise<WithRef<IAppointment> | undefined> {
    return snapshot(Patient.lastCompletedAppointment$(patient));
  }

  static async getNextScheduledAppointment(
    patient: WithRef<IPatient>
  ): Promise<WithRef<IAppointment> | undefined> {
    return Firestore.firstResult(
      Patient.appointmentCol(patient),
      where('event.from', '>=', toTimestamp()),
      orderBy('event.from', 'asc')
    );
  }

  static nextScheduledAppointment$(
    patient: WithRef<IPatient>
  ): Observable<WithRef<IAppointment> | undefined> {
    return firstResult$(
      Patient.appointmentCol(patient),
      where('event.from', '>=', toTimestamp()),
      orderBy('event.from', 'asc')
    );
  }

  static getFutureAppointments$(
    patient: IReffable<IPatient>,
    dateFrom: Moment = moment()
  ): Observable<WithRef<IAppointment>[]> {
    return query$(
      Appointment.col(patient),
      where('event.from', '>=', toTimestamp(dateFrom)),
      orderBy('event.from', 'asc')
    );
  }

  static async getAppointmentsOrderByEvent(
    patient: WithRef<IPatient>
  ): Promise<WithRef<IAppointment>[]> {
    return query(Appointment.col(patient), orderBy('event.from', 'desc'));
  }

  static resolveContactDetails$(
    patient: WithRef<IPatient>
  ): Observable<WithRef<IBasePatient & IPatientContactDetails> | undefined> {
    return Brand.organisation$({
      ref: Patient.brandRef({ ref: patient.ref }),
    }).pipe(
      switchMap((organisation) => {
        const smsProvider = organisation.integrations?.smsProvider;
        if (
          isPatientContactDetails(patient) &&
          hasValidContactDetails(patient, organisation.region, smsProvider)
        ) {
          return of(patient as WithRef<IBasePatient & IPatientContactDetails>);
        }

        return Patient.resolvePrimaryContact$(patient).pipe(
          map((resolvedPatient) => {
            if (
              !isPatientContactDetails(resolvedPatient) ||
              !hasValidContactDetails(
                resolvedPatient,
                organisation.region,
                smsProvider
              )
            ) {
              return;
            }
            return {
              ...resolvedPatient,
              name: patient.name,
            };
          })
        );
      })
    );
  }

  static isNewPatient$(
    patient: IReffable<IPatient>,
    appointment?: DocumentReference<IAppointment>
  ): Observable<boolean> {
    if (!appointment) {
      return from(Patient.firstCheckedOutAppointment(patient)).pipe(
        map((checkedOutAppointment) => !checkedOutAppointment)
      );
    }
    return from(Patient.firstScheduledAppointment(patient)).pipe(
      map((firstAppointment) => isSameRef(appointment, firstAppointment))
    );
  }

  static async resolveContactDetails(
    ref: DocumentReference<IPatient>
  ): Promise<WithRef<IBasePatient & IPatientContactDetails> | undefined> {
    return snapshot(
      OrganisationCache.patients
        .doc$(ref)
        .pipe(switchMap((patient) => Patient.resolveContactDetails$(patient)))
    );
  }

  static resolvePrimaryContact$(
    patient: WithRef<IPatient>
  ): Observable<WithRef<IBasePatient & IPatientContactDetails> | undefined> {
    return isPatientWithPrimaryContact(patient)
      ? Patient.primaryContact$(patient)
      : of(undefined);
  }

  static async resolvePrimaryContact(
    patient: Pick<WithRef<IPatient>, 'contactNumbers' | 'ref'>
  ): Promise<WithRef<IBasePatient & IPatientContactDetails> | undefined> {
    return isPatientWithPrimaryContact(patient)
      ? Patient.primaryContact(patient)
      : undefined;
  }

  static async resolveContactNumbers(
    patient: Pick<WithRef<IPatient>, 'contactNumbers' | 'ref'>
  ): Promise<IContactNumber[]> {
    if (
      patient.contactNumbers &&
      hasContactableNumber(patient.contactNumbers)
    ) {
      return patient.contactNumbers;
    }

    const primaryPatient = await Patient.resolvePrimaryContact(patient);
    return primaryPatient?.contactNumbers ?? [];
  }

  static async resolveFirstContactNumber(
    patient: Pick<WithRef<IPatient>, 'contactNumbers' | 'ref'>
  ): Promise<string | undefined> {
    const primaryContactNumber = first(
      await Patient.resolveContactNumbers(patient)
    );
    return primaryContactNumber ? primaryContactNumber.number : undefined;
  }

  static async resolveMobileNumber(
    patient: WithRef<IPatient>
  ): Promise<string | undefined> {
    const mobile = await Patient.getMobileNumber(patient);
    if (mobile) {
      return mobile;
    }
    const primaryContact = await Patient.resolvePrimaryContact(patient);
    return primaryContact ? Patient.getMobileNumber(primaryContact) : undefined;
  }

  static async getMobileNumber(
    patient: WithRef<IPatient>
  ): Promise<string | undefined> {
    const organisation = await snapshot(
      Brand.organisation$({ ref: Patient.brandRef({ ref: patient.ref }) })
    );
    return findMobileNumber(
      patient.contactNumbers,
      organisation.region,
      organisation.integrations?.smsProvider
    );
  }

  static async saveAppointment(
    patient: WithRef<IPatient>,
    appointment: WithRef<IAppointment> | IAppointment,
    createdBy?: PrincipleCreatedBy
  ): Promise<WithRef<IAppointment>> {
    if (isWithRef(appointment)) {
      await saveDoc(appointment, undefined, undefined, undefined, createdBy);
      return appointment;
    }
    const appointmentRef = await addDoc(
      Appointment.col(patient),
      appointment,
      undefined,
      undefined,
      createdBy
    );
    return getDoc(appointmentRef);
  }

  static tasks$(
    patient: IReffable<IPatient>,
    brand: WithRef<IBrand>,
    statusFilter: TaskStatus[] = []
  ): Observable<WithRef<ITask>[]> {
    return Task.byBrand$(
      brand,
      ...compact([
        where('mentionRefs', 'array-contains', patient.ref),
        statusFilter.length ? where('status', 'in', statusFilter) : undefined,
      ])
    );
  }

  static labJobs$(
    patient: IReffable<IPatient>,
    brand: WithRef<IBrand>
  ): Observable<WithRef<ILabJob>[]> {
    return from(getDocs(Brand.practiceCol(brand))).pipe(
      switchMap((practices) =>
        safeCombineLatest(
          practices.map((practice) => LabJob.byPatient$(practice, patient.ref))
        )
      ),
      map(reduceToSingleArray)
    );
  }

  static allAutomations$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IAutomation<TreatmentStepAutomation>>[]> {
    return combineLatest([
      Patient.automations$(patient),
      TreatmentPlan.all$(patient).pipe(
        multiSwitchMap((plan) => TreatmentPlan.automations$(plan)),
        map(reduceToSingleArray)
      ),
    ]).pipe(map(reduceToSingleArray));
  }

  static isDisabledPatient(status: PatientStatus): boolean {
    return [PatientStatus.Blocked, PatientStatus.Deceased].includes(status);
  }

  static automationCol<
    T extends IAutomationResource = IGeneratedTask | IAutomatedNotification,
  >(patient: IReffable<IPatient>): CollectionReference<IAutomation<T>> {
    return subCollection<IAutomation<T>>(
      patient.ref,
      PatientCollection.Automations
    );
  }

  static automations$(
    patient: IReffable<IPatient>
  ): Observable<WithRef<IAutomation<TreatmentStepAutomation>>[]> {
    return all$(Patient.automationCol(patient));
  }

  static updateMetadata(
    patient: WithRef<IPatient>,
    metadataKey: string,
    metadataValue: PatientMetadataValue
  ): IPatientMetadata {
    const metadata: IPatientMetadata = patient.metadata ?? {};
    set(metadata, metadataKey, metadataValue);
    return metadata;
  }

  static getMetadata(
    patient: IPatient,
    metadataKey: string
  ): PatientMetadataValue | undefined {
    const metadata = patient.metadata ?? {};
    return get(metadata, metadataKey) as PatientMetadataValue | undefined;
  }

  static getCampaignEntries$(
    patientRef: DocumentReference<IPatient>
  ): Observable<WithRef<IMarketingCampaignEntry>[]> {
    const brandRef = Firestore.getParentDocRef<IBrand>(patientRef);
    const entryQuery = collectionGroupQuery<IMarketingCampaignEntry>(
      MarketingCampaignCollection.MarketingCampaignEntries,
      where('brandRef', '==', brandRef),
      where('patient.ref', '==', patientRef)
    );
    return all$(entryQuery).pipe(
      multiFilter(undeletedFilter),
      multiSort(sortByCreatedAt)
    );
  }

  static getMohCapitationBand(patient: WithRef<IPatient>): IMohCapitationBand {
    const school = patient.metadata?.moh?.school;
    if (!school || school.schoolNumber === MohSchoolException.NotEnrolled) {
      return NOT_ENROLLED_MOH_CAPITATION_BAND;
    }

    const eqi = school.eqiNumber;
    if (!eqi) {
      return DEFAULT_MOH_CAPITATION_BAND;
    }

    const foundBand = Object.values(MOH_CAPITATION_BANDS).find(
      (band) => eqi >= band.eqiLow && eqi <= band.eqiHigh
    );
    return foundBand ?? DEFAULT_MOH_CAPITATION_BAND;
  }

  static async unsubscribe(
    patient: WithRef<IPatient>,
    campaignEntry?: WithRef<IMarketingCampaignEntry>
  ): Promise<void> {
    await Firestore.patchDoc(patient.ref, {
      metadata: Patient.updateMetadata(
        patient,
        RECEIVE_MARKETING_METADATA_KEY,
        false
      ),
    });

    if (campaignEntry) {
      await MarketingCampaignEntry.unsubscribe(campaignEntry);
      return;
    }

    const campaignEntries = await snapshot(
      Patient.getCampaignEntries$(patient.ref)
    );

    await asyncForEach(campaignEntries, async (entry) =>
      MarketingCampaignEntry.unsubscribe(entry)
    );
  }

  static async generateUnsubscribeToken(
    patientRef: DocumentReference<IPatient>,
    practiceRef: DocumentReference<IPractice>,
    campaignEntryRef?: DocumentReference<IMarketingCampaignEntry>
  ): Promise<string> {
    const brandRef = Firestore.getParentDocRef<IBrand>(patientRef);
    const organisationRef = Firestore.getParentDocRef<IOrganisation>(brandRef);
    const tokenExpiry = moment().add(3, 'months');
    const tokenData: IEmailReplyTokenData = {
      uid: uid(),
      practiceRef,
      campaignEntry: campaignEntryRef,
      actionType: EmailActionType.Unsubscribe,
      resourceType: EmailResourceType.Patient,
      resourceRef: patientRef,
    };
    const csrf = await createCSRF(
      Organisation.temporaryTokenCol(asReffable(organisationRef)),
      tokenData,
      tokenExpiry,
      moment(tokenExpiry).add(1, 'week'),
      true
    );

    return csrf.tokenUid;
  }
}

export function patientDataToSimilarQuery(
  patientData: ISuggestedPatientData
): string {
  const contactNumbers = compact(
    flatMap(patientData.contactNumbers, ({ number }) =>
      number ? getPhoneNumberVariants(number, patientData.region) : undefined
    )
  );

  const queryParams = compact([
    !patientData.name || patientData.name.length < 3
      ? undefined
      : patientData.name,
    !!patientData.dateOfBirth &&
      toMoment(patientData.dateOfBirth).format(DAY_MONTH_YEAR_FORMAT),
    ...contactNumbers,
  ]);

  return queryParams.length ? queryParams.join(' ') : '';
}
